
import React, { useState, useEffect } from 'react';
import { Braces, FileCode, Plus, Trash2, Play, AlertCircle, ArrowRight, FileText, Code2 } from 'lucide-react';
import { WorkflowNodeData, ExtractionRule } from '../../types';
import { extractData, detectContentType } from '../../utils/dataExtraction';

interface ResponseExtractorProps {
    data: WorkflowNodeData;
    onChange: (key: string, value: any) => void;
}

export const ResponseExtractor: React.FC<ResponseExtractorProps> = ({ data, onChange }) => {
    const [testResult, setTestResult] = useState<Record<string, any> | null>(null);
    const rules: ExtractionRule[] = data.extractionRules || [];
    const [detectedType, setDetectedType] = useState<'JSON' | 'XML' | 'Text'>('Text');

    useEffect(() => {
        if (data.exampleResponse) {
            setDetectedType(detectContentType(data.exampleResponse));
        }
    }, [data.exampleResponse]);

    const handleAddRule = () => {
        // Default method based on detected content type
        let defaultMethod: ExtractionRule['method'] = 'regex';
        if (detectedType === 'JSON') defaultMethod = 'jsonpath';
        if (detectedType === 'XML') defaultMethod = 'xpath';

        const newRule: ExtractionRule = { 
            method: defaultMethod, 
            source: '', 
            targetVar: ''
            // type is optional now, defaulting to auto/dynamic
        };
        onChange('extractionRules', [...rules, newRule]);
    };

    const handleRemoveRule = (index: number) => {
        const newRules = rules.filter((_, i) => i !== index);
        onChange('extractionRules', newRules);
    };

    const handleRuleChange = (index: number, field: keyof ExtractionRule, value: any) => {
        const newRules = [...rules];
        newRules[index] = { ...newRules[index], [field]: value };
        onChange('extractionRules', newRules);
    };

    const handleTest = () => {
        if (!data.exampleResponse) return;
        const result = extractData(data.exampleResponse, rules);
        setTestResult(result);
    };

    const getTypeColor = () => {
        if (detectedType === 'JSON') return 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400';
        if (detectedType === 'XML') return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400';
        return 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-400';
    };

    return (
        <div className="space-y-6 pt-4 border-t border-slate-200 dark:border-slate-800">
            <div className="flex items-center gap-2 mb-2">
                <div className="p-1.5 bg-slate-100 dark:bg-slate-800 rounded">
                    <Braces size={16} className="text-slate-600 dark:text-slate-300" />
                </div>
                <div>
                    <h4 className="text-xs font-bold text-slate-700 dark:text-slate-200 uppercase tracking-wider">Response Extraction</h4>
                    <p className="text-[10px] text-slate-500">Map response data to workflow variables.</p>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Left: Source Configuration */}
                <div className="space-y-3">
                    <div className="flex justify-between items-center">
                        <label className="text-[10px] font-bold text-slate-500 uppercase">Example Response</label>
                        <div className={`px-2 py-0.5 text-[9px] font-bold uppercase rounded ${getTypeColor()}`}>
                            Detected: {detectedType}
                        </div>
                    </div>
                    
                    <textarea
                        value={data.exampleResponse || ''}
                        onChange={(e) => onChange('exampleResponse', e.target.value)}
                        placeholder="Paste sample response here (JSON, XML, or Text)..."
                        className="w-full h-48 px-3 py-2 text-xs font-mono border rounded-lg bg-slate-50 dark:bg-slate-950 border-slate-300 dark:border-slate-700 focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500/20 resize-none"
                        spellCheck={false}
                    />
                </div>

                {/* Right: Rules */}
                <div className="space-y-3 flex flex-col">
                    <div className="flex justify-between items-center h-[26px]">
                        <label className="text-[10px] font-bold text-slate-500 uppercase">Extraction Rules</label>
                        <button 
                            onClick={handleAddRule}
                            className="text-[10px] flex items-center gap-1 text-teal-600 font-bold hover:bg-teal-50 dark:hover:bg-teal-900/30 px-2 py-1 rounded transition-colors"
                        >
                            <Plus size={10} /> Add Rule
                        </button>
                    </div>

                    <div className="flex-1 overflow-y-auto min-h-[120px] space-y-2 bg-slate-50 dark:bg-slate-950/50 rounded-lg p-2 border border-slate-200 dark:border-slate-800">
                        {rules.length === 0 && (
                            <div className="flex flex-col items-center justify-center h-full text-slate-400 text-xs italic opacity-70">
                                <FileCode size={20} className="mb-1" />
                                No rules defined
                            </div>
                        )}
                        
                        {rules.map((rule, idx) => (
                            <div key={idx} className="p-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded shadow-sm group">
                                <div className="flex gap-2 mb-2 items-center">
                                    <select
                                        value={rule.method}
                                        onChange={(e) => handleRuleChange(idx, 'method', e.target.value)}
                                        className="w-24 px-1 py-1 text-[10px] font-bold uppercase border rounded bg-slate-50 dark:bg-slate-950 border-slate-300 dark:border-slate-700 outline-none text-slate-600 dark:text-slate-400"
                                    >
                                        <option value="jsonpath">JSON Path</option>
                                        <option value="xpath">XPath/Tag</option>
                                        <option value="regex">Regex</option>
                                    </select>
                                    <div className="flex-1">
                                        <input 
                                            type="text" 
                                            value={rule.source}
                                            onChange={(e) => handleRuleChange(idx, 'source', e.target.value)}
                                            placeholder={rule.method === 'regex' ? 'Pattern e.g. ID: (\\d+)' : rule.method === 'xpath' ? 'Selector e.g. user > id' : 'Path e.g. data.id'}
                                            className="w-full px-2 py-1 text-xs border rounded bg-slate-50 dark:bg-slate-950 border-slate-300 dark:border-slate-700 focus:border-teal-500 outline-none font-mono placeholder:text-slate-300"
                                        />
                                    </div>
                                    <button 
                                        onClick={() => handleRemoveRule(idx)}
                                        className="p-1 text-slate-300 hover:text-red-500 transition-colors"
                                    >
                                        <Trash2 size={12} />
                                    </button>
                                </div>
                                <div className="flex gap-2 items-center">
                                    <ArrowRight size={10} className="text-slate-300 shrink-0" />
                                    <input 
                                        type="text" 
                                        value={rule.targetVar}
                                        onChange={(e) => handleRuleChange(idx, 'targetVar', e.target.value)}
                                        placeholder="Variable Name"
                                        className="flex-1 px-2 py-1 text-xs border rounded bg-slate-50 dark:bg-slate-950 border-slate-300 dark:border-slate-700 focus:border-teal-500 outline-none font-bold text-teal-700 dark:text-teal-400"
                                    />
                                    <div className="text-[9px] text-slate-400 font-mono px-2">
                                        (Auto)
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="flex justify-end pt-1">
                        <button 
                            onClick={handleTest}
                            disabled={!data.exampleResponse || rules.length === 0}
                            className="text-xs bg-slate-800 hover:bg-slate-900 text-white px-3 py-1.5 rounded flex items-center gap-1.5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <Play size={10} /> Test Extraction
                        </button>
                    </div>
                </div>
            </div>

            {testResult && (
                <div className="mt-4 p-3 bg-teal-50 dark:bg-teal-900/20 border border-teal-100 dark:border-teal-900 rounded-lg animate-in fade-in slide-in-from-top-1">
                    <h5 className="text-[10px] font-bold text-teal-800 dark:text-teal-300 uppercase mb-2">Extraction Result</h5>
                    <div className="grid gap-1">
                        {Object.entries(testResult).map(([key, val]) => (
                            <div key={key} className="flex text-xs font-mono items-center gap-2">
                                <span className="text-teal-700 dark:text-teal-400 min-w-[80px] shrink-0 truncate font-bold">{key}:</span>
                                <span className="text-slate-600 dark:text-slate-300 break-all">{typeof val === 'object' ? JSON.stringify(val) : String(val)}</span>
                                <span className="text-[9px] text-slate-400 bg-white dark:bg-slate-800 px-1 rounded border border-slate-100 dark:border-slate-800">
                                    {Array.isArray(val) ? 'Array' : typeof val}
                                </span>
                            </div>
                        ))}
                        {Object.keys(testResult).length === 0 && (
                            <div className="text-xs text-amber-600 flex items-center gap-1">
                                <AlertCircle size={12} /> No matches found. Check your paths/regex.
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};